/**
 * LandingPage.tsx  -  Public marketing page.
 * Shown at "/" for unauthenticated visitors.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Bot,
  Building2,
  CalendarCheck,
  Check,
  Gamepad2,
  Globe,
  HeartHandshake,
  Menu,
  Radio,
  Smartphone,
  Users,
  X,
} from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import LitlingsMark from "@/components/LitlingsMark";

// ─── Nav ──────────────────────────────────────────────────────────────────────

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const links = [
    ["#how", "How it works"],
    ["#features", "Features"],
    ["#mission", "Mission"],
    ["#pricing", "Pricing"],
  ];

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur transition-shadow ${
        scrolled ? "shadow-[0_1px_0_#EAEAE6]" : ""
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-5">
        <Link to="/" className="flex items-center gap-2.5">
          <LitlingsMark size={30} />
          <span className="font-display text-xl font-extrabold tracking-tight">
            Litlings
          </span>
        </Link>
        <div className="hidden items-center gap-7 text-[15px] font-semibold text-portal-text-soft md:flex">
          {links.map(([href, label]) => (
            <a key={href} href={href} className="hover:text-portal-text">
              {label}
            </a>
          ))}
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <Link to="/login" className="btn-ghost text-[15px] text-portal-text">
            Log in
          </Link>
          <Link to="/signup" className="btn-primary">
            Start free trial
          </Link>
        </div>
        <button
          className="rounded-full p-2 md:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="space-y-1 border-t border-portal-border bg-white px-5 pb-5 pt-3 md:hidden">
          {links.map(([href, label]) => (
            <a
              key={href}
              href={href}
              onClick={() => setOpen(false)}
              className="block rounded-xl px-3 py-2.5 font-semibold hover:bg-portal-bg-alt"
            >
              {label}
            </a>
          ))}
          <div className="flex gap-2 pt-2">
            <Link to="/login" className="btn-secondary flex-1">
              Log in
            </Link>
            <Link to="/signup" className="btn-primary flex-1">
              Free trial
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

// ─── Hero: the class screen itself is the picture ────────────────────────────

const PREVIEW_KIDS = [
  ["Aung Aung", "#F2681F", 3],
  ["Su Su", "#2D5BD7", 2],
  ["Min Min", "#257A4E", 0],
  ["Thida", "#D6453C", 4],
  ["Kyaw Kyaw", "#B98A00", 1],
  ["Nilar", "#2D5BD7", 2],
  ["Hla Hla", "#F2681F", 3],
  ["Ei Ei", "#257A4E", 1],
] as const;

function ClassPreview() {
  return (
    <div
      aria-hidden="true"
      className="relative rounded-[22px] border border-portal-border bg-white p-4 shadow-panel sm:p-5"
    >
      <div className="mb-4 flex items-center justify-between gap-2">
        <div>
          <p className="font-display text-lg font-extrabold leading-tight">
            Year 2 Sunflowers
          </p>
          <p className="text-xs text-portal-muted">12 children · 11 in today</p>
        </div>
        <span className="rounded-full bg-brand-ink px-3 py-1.5 text-xs font-semibold text-white">
          Whole class
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {PREVIEW_KIDS.map(([name, colour, pts], i) => (
          <div
            key={name}
            className={`relative flex flex-col items-center gap-1 rounded-[14px] px-1 pb-2 pt-3 ${
              i === 3 ? "bg-brand-yellow" : "bg-portal-bg-alt"
            }`}
          >
            <span
              className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: colour }}
            >
              {name[0]}
            </span>
            {pts > 0 && (
              <span className="absolute right-2 top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-ink px-1 text-[10px] font-bold text-white">
                {pts}
              </span>
            )}
            <span className="w-full truncate text-center text-[11px] font-semibold">
              {name}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 rounded-full bg-brand-yellow px-4 py-2 text-sm font-bold">
        <Check className="h-4 w-4" />
        +1 Great reading for Thida
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden px-5 pb-20 pt-28 sm:pt-36">
      <div
        aria-hidden="true"
        className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-yellow sm:h-96 sm:w-96"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-10 -left-24 hidden h-36 w-36 rotate-12 bg-brand-red xl:block"
      />
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <p className="mb-5 inline-flex items-center gap-2 rounded-full bg-brand-blue-soft px-4 py-1.5 text-sm font-semibold text-brand-blue">
            <span className="h-2 w-2 rounded-full bg-brand-blue" />
            Phonics for primary schools and tutors
          </p>
          <h1 className="font-display text-[2.75rem] font-extrabold leading-[1.02] tracking-[-0.035em] sm:text-6xl lg:text-[4.25rem]">
            Every child <span className="hl">deserves</span> to learn to read.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-portal-text-soft">
            Litlings is the simple way to teach phonics. Your whole class on one
            screen, the register in one tap, marking in seconds, and parents
            kept in the loop. From London to rural Myanmar, on any device.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup" className="btn-primary px-7 py-3.5 text-base">
              Start your 60-day free trial
            </Link>
            <Link to="/login" className="btn-secondary px-7 py-3.5 text-base">
              Log in
            </Link>
          </div>
          <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-portal-muted">
            {[
              "No credit card required",
              "Works on any device",
              "Free for NGOs and rural schools",
            ].map((t) => (
              <li key={t} className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-success" />
                {t}
              </li>
            ))}
          </ul>
        </div>
        <ClassPreview />
      </div>
    </section>
  );
}

// ─── How it works: the three daily jobs ──────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      tone: "tile-blue",
      n: "1",
      title: "Your class on one screen",
      desc: "Every child at a glance. Tap a child to give a point for great reading, blending or kindness. Whole-class points, a random picker and a timer are always one tap away.",
    },
    {
      tone: "tile-yellow",
      n: "2",
      title: "The register in one tap",
      desc: "Litlings fills in the register from who joined the live lesson. You check it, change anyone with a tap, and save. The school office sees every class instantly.",
    },
    {
      tone: "tile-red",
      n: "3",
      title: "Marking in seconds",
      desc: "Children photograph their work or record their voice. Litlings suggests a score, you confirm it, and parents see the result.",
    },
  ];
  return (
    <section id="how" className="bg-portal-bg-alt px-5 py-24">
      <div className="mx-auto max-w-6xl">
        <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
          Three jobs. <span className="hl">One tap</span> each.
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-portal-text-soft">
          Teachers told us classroom software asks too much of them. So Litlings
          does the first pass and you just confirm.
        </p>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {steps.map((s) => (
            <div key={s.n} className={`${s.tone} flex flex-col gap-3 p-7`}>
              <span className="font-display text-5xl font-extrabold leading-none opacity-90">
                {s.n}
              </span>
              <h3 className="font-display text-2xl font-extrabold leading-tight">
                {s.title}
              </h3>
              <p className="leading-relaxed opacity-90">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Features ─────────────────────────────────────────────────────────────────

const FEATURES = [
  {
    Icon: Gamepad2,
    title: "Research-backed games",
    desc: "Alphabet Tap, Sound Sorter, Rhyme Match, Boss Battles and more, all grounded in the Science of Reading.",
  },
  {
    Icon: Radio,
    title: "Live lessons",
    desc: "Push games and content to every child's screen in real time. See who is stuck, with hand-raise alerts and video built in.",
  },
  {
    Icon: Bot,
    title: "AI-assisted marking",
    desc: "Camera work and voice attempts are pre-scored. Teachers confirm, override and add voice feedback in one click.",
  },
  {
    Icon: BarChart3,
    title: "Mastery tracking",
    desc: "See exactly which sounds each child has mastered and which need practice.",
  },
  {
    Icon: CalendarCheck,
    title: "Attendance",
    desc: "A suggested register every lesson, a whole-school view for the office, and persistent absence flagged automatically.",
  },
  {
    Icon: Users,
    title: "Parent portal",
    desc: "Parents see their child's work, homework due dates and progress, and can message the teacher from any device.",
  },
  {
    Icon: Smartphone,
    title: "Any device, any bandwidth",
    desc: "Runs in the browser on low-cost phones and slow connections. No app to install.",
  },
  {
    Icon: Globe,
    title: "Built for the world",
    desc: "UK Letters and Sounds phonics, designed for EFL learners as well as native speakers.",
  },
];

function Features() {
  return (
    <section id="features" className="px-5 py-24">
      <div className="mx-auto max-w-6xl">
        <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
          Everything a phonics teacher needs,{" "}
          <span className="hl">nothing</span> they don't.
        </h2>
        <div className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => (
            <div key={f.title}>
              <div
                className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full ${
                  [
                    "bg-brand-blue text-white",
                    "bg-brand-yellow text-brand-ink",
                    "bg-brand-red text-white",
                  ][i % 3]
                }`}
              >
                <f.Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-lg font-bold">{f.title}</h3>
              <p className="mt-1.5 leading-relaxed text-portal-muted">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Mission ──────────────────────────────────────────────────────────────────

function Mission() {
  const stats = [
    [
      "60 days",
      "Free trial",
      "no credit card",
      "bg-brand-yellow text-brand-ink",
    ],
    [
      "80%",
      "NGO discount",
      "verified charities and schools",
      "bg-brand-red text-white",
    ],
    [
      "1 login",
      "Growing curriculum",
      "maths and writing next",
      "bg-white text-brand-ink",
    ],
    [
      "Any phone",
      "Low bandwidth",
      "built for everywhere",
      "bg-brand-ink text-white ring-1 ring-white/20",
    ],
  ];
  return (
    <section id="mission" className="bg-brand-blue px-5 py-24 text-white">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="mb-4 text-sm font-bold uppercase tracking-[0.14em] text-white/70">
            Our mission
          </p>
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
            Learning is a human right, not a privilege.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white/85">
            Hundreds of millions of children never get the education they
            deserve, not because they can't learn, but because the tools,
            training and resources never reach them. Litlings exists to put
            great learning in the hands of every child and every teacher, no
            matter where they are or what they can afford.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-white/85">
            We start with reading, the foundation of all learning. Maths,
            writing and a full English curriculum follow under the same login.
            Verified NGOs and under-resourced schools can apply for heavily
            subsidised access.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {stats.map(([n, label, sub, tone]) => (
            <div key={label} className={`rounded-[18px] p-6 ${tone}`}>
              <p className="font-display text-3xl font-extrabold leading-none">
                {n}
              </p>
              <p className="mt-3 font-bold">{label}</p>
              <p className="mt-0.5 text-sm opacity-75">{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

const PLANS = [
  {
    name: "Spark",
    price: "Free",
    sub: "Forever. Great for tutors and small groups",
    highlight: false,
    cta: "Get started free",
    features: [
      "Up to 40 pupils in total",
      "Unlimited classes and groups",
      "Class screen, points and register",
      "Phonics games and lessons",
      "Homework photos and teacher marking",
      "Parent messaging",
      "Digital certificates",
    ],
    not: ["AI pre-scoring", "School analytics"],
  },
  {
    name: "Grow",
    price: "£300",
    sub: "per year, or $390 USD",
    highlight: false,
    cta: "Start 60-day free trial",
    features: [
      "Up to 75 students",
      "All games and Boss Battles",
      "AI pre-scoring, 3,000 scores a month",
      "Unlimited manual marking",
      "Full parent portal and messaging",
      "Homework module",
      "Branded PDF certificates",
      "Email support",
    ],
    not: [],
  },
  {
    name: "School",
    price: "£690",
    sub: "per year, or $890 USD",
    highlight: true,
    cta: "Start 60-day free trial",
    features: [
      "Up to 250 students",
      "Everything in Grow, plus:",
      "AI pre-scoring, 10,000 scores a month",
      "Whole-school attendance view",
      "Class analytics heatmap",
      "Teacher Trainer role",
      "Custom certificate branding",
      "Priority email support",
    ],
    not: [],
  },
  {
    name: "Trust",
    price: "£1,490",
    sub: "per year, or $1,890 USD",
    highlight: false,
    cta: "Start 60-day free trial",
    features: [
      "Unlimited students and classes",
      "Everything in School, plus:",
      "Multi-school and trust management",
      "Highest AI quota",
      "High-availability hosting",
      "Onboarding help",
    ],
    not: [],
  },
];

function Pricing() {
  const navigate = useNavigate();
  return (
    <section id="pricing" className="px-5 py-24">
      <div className="mx-auto max-w-6xl">
        <h2 className="max-w-2xl font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] sm:text-5xl">
          Simple, <span className="hl">school-friendly</span> pricing.
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-portal-text-soft">
          Priced per school, billed yearly. Shown in GBP; pay in your local
          currency at checkout. 60-day free trial, no credit card.
        </p>

        <div className="mt-12 grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`relative flex flex-col rounded-[22px] p-7 ${
                plan.highlight ? "bg-brand-ink text-white" : "bg-portal-bg-alt"
              }`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-7 rounded-full bg-brand-yellow px-3 py-1 text-xs font-bold text-brand-ink">
                  Most popular
                </span>
              )}
              <p
                className={`text-sm font-bold uppercase tracking-[0.12em] ${plan.highlight ? "text-white/70" : "text-portal-muted"}`}
              >
                {plan.name}
              </p>
              <p className="mt-2 font-display text-4xl font-extrabold">
                {plan.price}
                {plan.price !== "Free" && (
                  <span
                    className={`text-base font-semibold ${plan.highlight ? "text-white/60" : "text-portal-muted"}`}
                  >
                    {" "}
                    /yr
                  </span>
                )}
              </p>
              <p
                className={`text-sm ${plan.highlight ? "text-white/60" : "text-portal-muted"}`}
              >
                {plan.sub}
              </p>
              <ul className="my-6 flex-1 space-y-2.5 text-sm">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check
                      className={`mt-0.5 h-4 w-4 flex-shrink-0 ${plan.highlight ? "text-brand-yellow" : "text-success"}`}
                    />
                    {f}
                  </li>
                ))}
                {plan.not.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-portal-muted-soft"
                  >
                    <X className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/signup")}
                className={
                  plan.highlight
                    ? "btn-accent w-full py-3"
                    : "btn-primary w-full py-3"
                }
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <div className="rounded-[22px] border border-portal-border p-7">
            <p className="flex items-center gap-2 font-display text-xl font-bold">
              <Building2 className="h-5 w-5" />
              Enterprise
            </p>
            <p className="mt-2 text-portal-muted">
              Multi-school operators, language school chains and government
              programmes. Custom pricing, a dedicated contact, white-label
              option and a 99.9% SLA.
            </p>
            <a
              href="mailto:hello@litlings.com?subject=Enterprise%20enquiry"
              className="btn-secondary mt-5"
            >
              Contact sales
            </a>
          </div>
          <div className="rounded-[22px] bg-brand-yellow-soft p-7">
            <p className="flex items-center gap-2 font-display text-xl font-bold">
              <HeartHandshake className="h-5 w-5" />
              Accessibility pricing
            </p>
            <p className="mt-2 text-portal-text-soft">
              Verified NGOs and charities get 80% off any plan. Under-resourced
              schools in developing regions get heavily subsidised access. We
              review each application individually.
            </p>
            <a
              href="mailto:hello@litlings.com?subject=Accessibility%20pricing"
              className="btn-primary mt-5"
            >
              Apply for assistance
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Closing call to action ──────────────────────────────────────────────────

function Closing() {
  return (
    <section className="px-5 pb-24">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 rounded-[28px] bg-brand-yellow p-10 sm:flex-row sm:items-center sm:p-14">
        <h2 className="max-w-xl font-display text-3xl font-extrabold leading-tight tracking-[-0.03em] sm:text-4xl">
          Try it with your class this week. It takes five minutes to set up.
        </h2>
        <Link
          to="/signup"
          className="btn-primary flex-shrink-0 px-7 py-3.5 text-base"
        >
          Start free trial
        </Link>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-portal-text">
      <Nav />
      <main id="main-content">
        <Hero />
        <HowItWorks />
        <Features />
        <Mission />
        <Pricing />
        <Closing />
      </main>
      <SiteFooter />
    </div>
  );
}
